import React from "react";
import { useRouter } from "next/router";
import styled, { createGlobalStyle } from "styled-components";
import { FlexContainer, FlexDiv } from "@/components/Atoms/Box";
import { Typography as DefaultTypography } from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";
import { space } from "styled-system";
import useDisableIntercom from "@/hooks/useDisableIntercom";

export const PolicyPageContent = (props: any) => {
  const { showHeader = false } = props || {};

  const location = useRouter();

  const isFromAppSource = location?.query;

  const showTopSpace = isFromAppSource?.source !== "app";

  return (
    <Container flexDirection={"column"}>
      {showHeader && (
        <Typography
          fontFamily={"var(--man-500)"}
          color={theme.colors.gray_100}
          variant="heading1"
        >
          Privacy Policy
        </Typography>
      )}
      <Wrapper flexDirection={"column"} showHeightGap={showTopSpace}>
        <FlexDiv style={{ gap: "10px" }}>
          <Typography
            fontFamily={"var(--man-600)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
            style={{
              fontStyle: "italic",
              margin: "0px",
            }}
          >
            Last updated:
          </Typography>
          <Typography
            fontFamily={"var(--man-500)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
          >
            February 06, 2024
          </Typography>
        </FlexDiv>
        <FlexDiv style={{ gap: "10px", marginBottom: "20px" }}>
          <Typography
            fontFamily={"var(--man-600)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
            style={{
              fontStyle: "italic",
            }}
          >
            Effective From:
          </Typography>
          <Typography
            fontFamily={"var(--man-500)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
          >
            February 06, 2024
          </Typography>
        </FlexDiv>

        <Typography
          fontFamily="var(--man-500)"
          variant="paragraph2"
          color={theme.colors.gray_100}
        >
          Blacktail Mindhouse Private Limited is a Software as a Service (SaaS)
          (<Strong>“Mindhouse”</Strong>, <Strong>“We”</Strong>,{" "}
          <Strong>“Us”</Strong>, or <Strong>“Our”</Strong>) that provides goods
          and services encompassing mental and physical well-being, including,
          but not limited to, physical fitness, mindful yoga, meditation-related
          training activities, nutrition, therapy, and consultations for
          habit-building, provided through the Platform (as defined below).
        </Typography>

        <OL mb={"25px"}>
          <Heading variant="paragraph2" color={theme.colors.gray_100}>
            In this Privacy Policy:
          </Heading>

          <AlphaList style={{ marginLeft: "50px" }}>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              The <Strong>“Platform”</Strong> includes{" "}
              <Typography
                fontFamily={"var(--man-500)"}
                variant="link1"
                link1Color={theme.colorsV2.blue.shade_800}
                href="https://www.betheshyft.com"
              >
                https://www.betheshyft.com,{" "}
              </Typography>
              <Typography
                fontFamily={"var(--man-500)"}
                variant="link1"
                link1Color={theme.colorsV2.blue.shade_800}
                href="https://www.dash.health"
              >
                https://www.dash.health,{" "}
              </Typography>
              <Typography
                fontFamily={"var(--man-500)"}
                variant="link1"
                link1Color={theme.colorsV2.blue.shade_800}
                href="https://www.mindhouse.com"
              >
                https://www.mindhouse.com{" "}
              </Typography>
              and any other service, product, app, or website provided by
              Mindhouse that explicitly specifies the applicability of this
              Privacy Policy.
            </Typography>
          </AlphaList>

          <AlphaList style={{ marginLeft: "50px" }}>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              <Strong>“Service”</Strong> means all products, and services
              (including but not limited to text, audio, video, and interactive
              content), that are availed by Customer or provided to Customer by
              Company through the Platform.
            </Typography>
          </AlphaList>

          <AlphaList style={{ marginLeft: "50px" }}>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Customers that purchase or obtain services from Mindhouse under
              Our <Strong>“Terms of Service”</Strong>, available at{" "}
              <Typography
                fontFamily={"var(--man-500)"}
                variant="link1"
                link1Color={theme.colorsV2.blue.shade_800}
                href="https://www.betheshyft.com/terms-condition/"
              >
                https://www.betheshyft.com/terms-condition/
              </Typography>
              , or another agreement with Mindhouse, are referred to as{" "}
              <Strong>“Customers”</Strong>.
            </Typography>
          </AlphaList>

          <AlphaList style={{ marginLeft: "50px" }}>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Individuals that use the services purchased by Customers are
              referred to as <Strong>“Users”</Strong>.
            </Typography>
          </AlphaList>

          <AlphaList style={{ marginLeft: "50px" }}>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              All visitors to Our Platform, whether they are Customers, Users,
              or any other visitor to the Platform, are referred to as
              <Strong>“Visitors”</Strong>.
            </Typography>
          </AlphaList>

          <AlphaList style={{ marginLeft: "50px" }}>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Collectively, the personal information about Customers (
              <Strong>“Customer Information”</Strong>), Users (
              <Strong>“User Information”</Strong>), and Visitors (
              <Strong>“Visitor Information”</Strong>) is referred to as
              “Personal Information”.
            </Typography>
          </AlphaList>

          <AlphaList style={{ marginLeft: "50px" }}>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Customers, Users, and Visitors are collectively referred to as
              <Strong>“You”</Strong> or <Strong>“Your”</Strong>.
            </Typography>
          </AlphaList>
        </OL>
        <OL>
          <Typography
            fontFamily={"var(--man-500)"}
            variant="paragraph2"
            color={theme.colors.gray_100}
          >
            At Mindhouse, protecting the privacy of Customers, Users and
            Visitors is a core business principle. We understand that the
            information provided by You belongs to You, not to Mindhouse.
          </Typography>

          <Typography
            fontFamily={"var(--man-500)"}
            variant="paragraph2"
            color={theme.colors.gray_100}
          >
            This Privacy Policy applies only to information collected by
            Mindhouse on or through the Platform. This Privacy Policy is subject
            to Mindhouse’s Terms of Service, as updated from time to time. If
            any capitalized word or phrase has been defined within the Terms of
            Service but has not been defined within this Privacy Policy, that
            word or phrase shall have the same meaning that has been assigned to
            it under the Terms of Service.
          </Typography>
          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              What Personal Information does Mindhouse collect?
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Mindhouse collects personal information about (1) Customers; (2)
              Users; and (3) Visitors. Collectively, the personal information
              about Customers (<Strong>“Customer Information”</Strong>), Users (
              <Strong>“User Information”</Strong>), and Visitors (
              <Strong>“Visitor Information”</Strong>) is referred to as{" "}
              <Strong>“Personal Information”</Strong>. If an individual
              purchases Services or uses the Platform directly on its own
              behalf, then such individual is both a Customer and a User.
            </Typography>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Customer:</Strong> When a Customer registers an
                  account for the Services, the Customer is required to provide
                  Personal Information that is necessary for Us to provide the
                  Services. We ask for Personal Information that includes, but
                  is not limited to, name, email address, location, IP address,
                  phone number, password, and company’s name.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>User:</Strong> When a User registers for a Service,
                  the User is required to provide Personal Information that is
                  necessary for Us to provide the Service. We ask for
                  information that includes but is not limited to, name, profile
                  picture, contact information including email address and phone
                  number, medical reports, heath goals, nutrition data, gender,
                  date of birth, location, IP address, insurance details, sleep
                  quality information, BMI, and password.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Visitor:</Strong> When a Visitor visits the Platform,
                  Mindhouse collects information as explained in Section 7
                  below. We may also collect other Personal Information that is
                  provided by the Visitor at the Visitor’s discretion, such as
                  when a Visitor contacts Us.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              How does Mindhouse process Personal Information?
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              The Personal Information collected is only used in accordance with
              this Privacy Policy for the purposes described below.
            </Typography>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Performance of Contract:</Strong> To provide Services
                  in the normal course of Our business, to provide features in
                  the Services availed, including contacting You regarding
                  technical support, creating personalized services for the
                  Customer, discussing billing issues, or bringing to Your
                  attention any aspect of Our mutual business such as changes to
                  Agreements or this Privacy Policy.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Legitimate Interest:</Strong> To protect Our legal
                  rights, to improve the performance, features and security of
                  the Platform and Services by analyzing user behavior, or for
                  detection or prevention of fraud or misuse of Our Services or
                  properties.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Vital Interests:</Strong> If and as necessary to
                  protect Your vital interests or the vital interests of another
                  natural person, such as if required to provide medical aid.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Compliance with a Legal Obligation:</Strong> To comply
                  with Our legal obligations and for law enforcement purposes,
                  for compliance with a legal requirement or court order.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>With Consent:</Strong> To provide Our newsletter(s),
                  if You subscribe for a newsletter.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Who does Mindhouse share Personal Information with?
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Mindhouse does not use or disclose Personal Information for
              purposes that are materially different from those described in
              this Privacy Policy. We do not sell Personal Information to any
              third party. We will share the Personal Information with third
              parties only in the ways that are described in this Privacy
              Policy.
            </Typography>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>User Information with Customer:</Strong> We provide
                  health-based Services to Customers who may provide access to
                  the Users. Accordingly, Mindhouse may provide access to the
                  Customer with User Information and reports generated using the
                  Services.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Service Providers:</Strong> Our service providers and
                  Affiliates provide services to Us and Our Customers. Our
                  service providers and Affiliates will be required to use
                  Personal Information solely for the purpose of providing the
                  Services.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Vendor:</Strong> Vendors are third parties that
                  provide services to You on or through the Platform. Vendors
                  are contractually required to use Personal Information solely
                  for the purpose of providing the Services.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>For Legal Reasons:</Strong> We may disclose Personal
                  Information as required by law such as to respond to
                  subpoenas, court orders, or legal processes, or to establish
                  or exercise legal rights or defend against legal claims and
                  when We believe, in good faith, that disclosure is necessary
                  to protect Our right, or to protect Your safety or the safety
                  of others. In certain situations, We may be required to
                  disclose Personal Information in response to lawful requests
                  by public authorities, including to meet national security or
                  law enforcement requirements. We may also share Personal
                  Information to investigate, prevent, or take action regarding
                  illegal activities, suspected fraud, situations involving
                  potential threats to the physical safety of any person,
                  violations of Our Terms of Service, or as otherwise required
                  by law.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Merger and Acquisition:</Strong> If Mindhouse is
                  involved in merger, acquisition, or sale of all or a portion
                  of its assets, Customer will be notified via email and/or a
                  prominent notice on the Platform or Service of any changes in
                  ownership, processing of Personal Information, and choices
                  available to Customer regarding any Personal Information in
                  Our possession.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              What rights do You have under this Privacy Policy?
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You may exercise Your rights by contacting Us at{" "}
              <Typography
                fontFamily={"var(--man-500)"}
                variant="link1"
                link1Color={theme.colorsV2.blue.shade_600}
                href="mailto:help@betheshyft.com"
              >
                help@betheshyft.com.
              </Typography>{" "}
              We may require You to furnish proof of Your identity and/or proof
              of authorization to undertake the requested action before granting
              any request under this section. Where permitted by law, We may
              charge a fee for the performance of the obligations under this
              section.
            </Typography>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You may exercise the following rights for Your Personal
              Information:
            </Typography>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Data Ownership:</Strong> You own all personally
                  identifiable information that You bring to Our platform. We
                  may Process the Personal Information in accordance with this
                  Privacy Policy.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Right to Data Portability:</Strong> Data may be
                  exported from Our system by Customers for external use in a
                  format chosen by the Customer from a list of formats provided
                  by Us.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Right to Erasure:</Strong> Mindhouse deletes Personal
                  Information subject to Section 5 below. In certain situations,
                  We may retain some or all of Your Personal Information in
                  accordance with Section 5 below. We may refuse to grant this
                  request if We consider the retention of Personal Information
                  to be necessary for Us to protect Our legal rights, for law
                  enforcement purposes, for compliance with a legal requirement
                  or court order, or for detection or prevention of fraud or
                  misuse of Our Service or properties.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Right to Access and Correction:</Strong> Upon Your
                  request, We will inform You whether We hold any of Your
                  Personal Information, and if yes, what Personal Information We
                  have. Some categories of Your Personal Information can be
                  modified in Your account. For Personal Information that cannot
                  be modified through Your account, please contact Us.
                  Unmodified Personal Information may remain in back-ups and
                  records subject to Section 5 below.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>
                    Right to Opt-Out from Direct Marketing Communications:
                  </Strong>{" "}
                  All direct marketing communications, such as emails, sent by
                  Us contain instructions for unsubscribing from such
                  communication. Alternatively, You may contact Us to stop
                  sending direct marketing communications to You. We may
                  continue to send service updates even if you opt out of direct
                  marketing communications.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Data transfer, storage and retention.
            </Heading>

            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Data Transfer and Storage:</Strong> By accessing the
                  Platform or using the Services, You agree and provide Your
                  consent to transfer and store Personal Information, including
                  personally identifiable information and sensitive personal
                  data, to Our servers located in Mumbai.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Data Retention:</Strong> Personal Information
                  submitted by You for the purpose of seeking information about
                  Our services and products will be retained for a period of up
                  to one (1) year, and will thereafter be deleted or anonymized
                  unless You subscribe to a service or purchase a product or
                  service. Personal Information of customers that have purchased
                  a product or service or subscribed to a service will not be
                  deleted automatically. If You request deletion of Your
                  Personal Information under Section 4.3 above, We will delete
                  Your Personal Information, except that Mindhouse may retain
                  Personal Information as described below:
                </Typography>
              </LI>
              <OL>
                <LI>
                  <Typography
                    fontFamily={"var(--man-500)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Legitimate Interest:</Strong> Personal Information
                    necessary for Our legitimate purposes, such as record
                    keeping, resolution of disputes, enforcement of Our
                    agreements, protection of Our rights, prevention and
                    detection of fraud, or misuse of Our Services or properties,
                    may be retained as long as necessary for such legitimate
                    interests. We will not retain data for such legitimate
                    purposes if We believe that Your interests or fundamental
                    rights and freedoms override Our legitimate interests.
                  </Typography>
                </LI>

                <LI>
                  <Typography
                    fontFamily={"var(--man-500)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Legal Obligation:</Strong> Personal Information
                    necessary to fulfil Our legal obligations, such as taxation,
                    data retention, reporting obligations, and other obligations
                    under Applicable Law, court order, or law enforcement
                    request, may be retained as long as required to comply with
                    such legal obligations.
                  </Typography>
                </LI>

                <LI>
                  <Typography
                    fontFamily={"var(--man-500)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    <Strong>Anonymized Data for Service Improvement:</Strong> We
                    may retain deidentified or anonymized data for improving
                    Services, and for creating new Services.
                  </Typography>
                </LI>
              </OL>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              How does Mindhouse protect Personal Information?
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Mindhouse undertakes commercially reasonable measures to protect
              Personal Information. To prevent unwanted and unauthorized access
              to Personal Information, Mindhouse uses the following methods,
              techniques, and technologies:
            </Typography>

            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  We use security logs, train Our personnel, and limit access to
                  only essential personnel.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  During transmission, the Personal Information is protected
                  with encryption.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  The Service is hosted in a secure server environment that uses
                  reasonable technologies to prevent access from outside
                  intruders.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Passwords are hashed before being stored.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Sensitive personal information, such as Your health records,
                  are protected with encryption at rest.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Cookies, web beacons, and other tracking technology.
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              As is true of most websites and services, We gather certain
              information automatically and store it in log files to improve Our
              Services’ features and security. This information may include
              Internet Protocol (IP) addresses, device model, device operating
              system, internet service provider (ISP), referring/exit pages,
              date/time stamp, and/or clickstream data, among others. We do not
              link this automatically-collected data to other information We
              collect about You.
            </Typography>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              We, and third parties on the Platform, also use cookies and other
              tracking technologies. For information about the use of cookies
              and other tracking technologies on the Platform and Service(s),
              please see Our Cookie Policy, available at{" "}
              <Typography
                fontFamily={"var(--man-500)"}
                variant="link1"
                link1Color={theme.colorsV2.blue.shade_600}
                href="https://www.betheshyft.com/cookie-policy/"
              >
                https://www.betheshyft.com/cookie-policy/
              </Typography>{" "}
              , as updated from time to time, and incorporated herein by
              reference.
            </Typography>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You can control the use of cookies as described in the Cookie
              Policy, but disabling cookies may limit Your use of certain
              features or functions on the Platform or Service(s).
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Third party websites, apps, software, or services.
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              The Platform may, from time to time, contain links to the websites
              of Our partner networks, advertisers, affiliates, or any other
              third-party websites, apps, or services. This Privacy Policy does
              not apply to data collected on third-party websites, apps, or
              services, or the use of Mindhouse’s Services by third parties for
              the collection of information, even if such third parties are
              Mindhouse’s partners and display Mindhouse’s branding on their
              websites, apps, or services. This Privacy Policy further does not
              apply to data of Users that is collected by Customers. Data
              collected by third parties or Customers is subject to their own
              privacy policies and privacy practices. Please check the privacy
              policies and terms of service or terms and conditions of the
              applicable Customer (if You are a User), or the applicable
              third-party website, app, software or service that You choose to
              visit or use. We do not accept any liability for any collection,
              storage, use, misuse, sharing, disclosure or transfer of data,
              viruses or other malware, or any other damage, loss or harm caused
              to You or anyone else as a result of Your visit to any third-party
              website, app, or service, use of any third-party software, or the
              privacy or data protection practices of Our Customers.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Children’s privacy.
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              We understand the importance of taking extra precautions to
              protect the privacy and safety of anyone under the age of minority
              in the applicable jurisdiction (<Strong>“Minor”</Strong>) using
              Our Platform or Services. Nutrition for kids is the only Service
              that is intended for the benefit of Minors. For only such
              Services, We collect Personal Information of a Minor subject to
              the parent or legal guardian’s consent. Apart from the above-named
              service, Our Platform, along with the information it offers, is
              not intended for a Minor, and We do not knowingly collect, handle,
              or retain, any Personal Information from Minor.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Do not track.
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Our website currently does not respond to Do Not Track requests
              from web browsers or other software.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Modifications to this Privacy Policy.
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              As We are constantly improving the Services and expanding the
              business, We retain the right to modify the Privacy Policy at any
              time and from time to time. In the event that We make any material
              modification, We will notify You about the modification by sending
              an email to the email address provided by You in Your account, by
              means of a notice on the Platform or Service, and/or through any
              other reasonable means. The modification will become effective at
              least fourteen (14) days after the new policy has been notified or
              posted on the Platform or on the Service. We encourage You to
              periodically review this page for up-to-date information on Our
              privacy practices.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              Additional information for certain regions.
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>General Information.</Strong> This Section 12 contains
                  rights that Customers, Users and Visitors from certain regions
                  have in addition to the rights listed in Section 4 above.
                  Customers, Users and Visitors from the following regions have
                  the additional rights listed in the following Sections: (i)
                  United Kingdom: Section 12.2; (ii) Singapore: Section 12.3;
                  United Arab Emirates (UAE): Section 12.4; and India: Section
                  12.5. We may require You to furnish proof of Your identity
                  and/or proof of authorization to undertake the requested
                  action prior to granting any request under this Section. Where
                  permitted by law, Mindhouse may charge a fee for performance
                  of its obligations under this Section 12.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>United Kingdom.</Strong> This information is
                  applicable for Customers, Users and Visitors located in the
                  United Kingdom only. Mindhouse is committed to protecting all
                  personal data received from the United Kingdom in compliance
                  with UK’s Data Protection Act 2018 and UK-GDPR. You have the
                  following rights available to You in addition to the rights
                  listed in Section 4 above:
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Right to Restrict Processing:</Strong> You may
                      request for processing of Your data to be restricted under
                      Article 18 of UK-GDPR.
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Right to Withdraw Consent:</Strong> You may
                      withdraw Your consent, preventing future data processing
                      based on that consent.
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Right to Object:</Strong> You may object to the
                      processing of Your Personal Information under Article 21
                      of UK-GDPR.
                    </Typography>
                  </LI>

                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Supervisory Authority:</Strong> If you are
                      unsatisfied with Our response to Your request, You have
                      the right to complain to Your supervisory authority.
                      However, for speedy fulfilment of Your rights, we request
                      that you reach out to Us at{" "}
                      <Typography
                        fontFamily={"var(--man-500)"}
                        variant="link1"
                        link1Color={theme.colorsV2.blue.shade_600}
                        href="mailto:help@betheshyft.com"
                      >
                        help@betheshyft.com.
                      </Typography>{" "}
                      , so that We can help You resolve any issue You may have
                      faced in the fulfilment of Your request.
                    </Typography>
                  </LI>
                  <Typography
                    fontFamily={"var(--man-500)"}
                    variant="paragraph2"
                    color={theme.colors.gray_100}
                  >
                    Mindhouse uses data processors listed at
                    https://www.betheshyft.com/data-processing-agreement (as
                    updated from time to time) for processing of information.
                  </Typography>
                </OL>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Singapore.</Strong> This information is applicable for
                  Customers, Users and Visitors located in Singapore only. You
                  have the following rights available to You in addition to the
                  rights listed in Section 4 above:
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Right to Object:</Strong> You may object to the
                      processing of Your Personal Information under Section 16
                      Singapore Personal Data Protection Act 2012.
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Supervisory Authority:</Strong> If you are
                      unsatisfied with Our response to Your request, You have
                      the right to complain to Your supervisory authority.
                      However, for speedy fulfilment of Your rights, we request
                      that you reach out to Us at{" "}
                      <Typography
                        fontFamily={"var(--man-500)"}
                        variant="link1"
                        link1Color={theme.colorsV2.blue.shade_600}
                        href="mailto:help@betheshyft.com"
                      >
                        help@betheshyft.com.
                      </Typography>{" "}
                      , so that We can help You resolve any issue You may have
                      faced in the fulfilment of Your request.
                    </Typography>
                  </LI>
                </OL>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  UAE. This information is applicable for Customers, Users and
                  Visitors located in the United Arab Emirates only. You have
                  the following rights available to You in addition to the
                  rights listed in Section 4 above:
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Right to Object:</Strong> You may object to the
                      processing of Your Personal Information under Section 16
                      Singapore Personal Data Protection Act 2012.
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Supervisory Authority:</Strong> If you are
                      unsatisfied with Our response to Your request, You have
                      the right to complain to Your supervisory authority.
                      However, for speedy fulfilment of Your rights, we request
                      that you reach out to Us at{" "}
                      <Typography
                        fontFamily={"var(--man-500)"}
                        variant="link1"
                        link1Color={theme.colorsV2.blue.shade_600}
                        href="mailto:help@betheshyft.com"
                      >
                        help@betheshyft.com.
                      </Typography>{" "}
                      , so that We can help You resolve any issue You may have
                      faced in the fulfilment of Your request.
                    </Typography>
                  </LI>
                </OL>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  India. This information is applicable for Customers, Users and
                  Visitors in India only. You have the following rights
                  available to You in addition to the rights listed in Section 4
                  above:
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      <Strong>Right to Nominate:</Strong> You may designate an
                      alternate individual who, in the event of Your demise or
                      incapacitation, may duly exercise the rights conferred in
                      this Privacy Policy.
                    </Typography>
                  </LI>
                </OL>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Contact Us.
                </Typography>

                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--man-500)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      If You have questions or concerns about this Privacy
                      Policy, You can contact Us at{" "}
                      <Typography
                        fontFamily={"var(--man-500)"}
                        variant="link1"
                        link1Color={theme.colorsV2.blue.shade_600}
                        href="mailto:help@betheshyft.com"
                      >
                        help@betheshyft.com.
                      </Typography>
                    </Typography>
                  </LI>
                </OL>
              </LI>
            </OL>
          </LI>
        </OL>
      </Wrapper>
      <GlobalCss />
    </Container>
  );
};
function PrivacyPolicy() {
  useDisableIntercom({});
  return <PolicyPageContent />;
}

export default PrivacyPolicy;

const Heading = styled(DefaultTypography)`
  // text-decoration: underline;
  font-family: var(--man-700);
`;
const Typography = styled(DefaultTypography)`
  text-align: justify;
`;
const Container = styled(FlexContainer)`
  margin: 40px auto auto;
`;
const AlphaList: any = styled.li`
  list-style-type: lower-alpha;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const Wrapper = styled(FlexDiv)`
  padding: ${(props) => {
    return props.showHeightGap ? "110px 0px" : "10px 0px";
  }};
  p {
    margin-bottom: 11px;
    &:last-child {
      margin-bottom: 0px;
    }
  }
`;
const OL = styled.ol`
  ${space}
  ol {
    padding-inline-start: 40px !important;
  }
  counter-reset: item;
  > li {
    padding-left: 0px;
    li {
      padding-left: 0px;
    }
    ${AlphaList} {
      padding-left: 0;
    }
    ${Heading} {
      margin-bottom: 25px;
    }
  }
`;
const UL = styled.ul`
  ${space}
  ul {
    padding-inline-start: 40px !important;
  }
`;
const LI: any = styled.li`
  display: block;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
  position: relative;
  &:before {
    display: inline-block;
    float: left;
    margin-right: 5px;
    content: counters(item, ".") ".";
    counter-increment: item;
    font-size: 12px;
    font-family: var(--man-500);
    color: ${theme.colors.gray_100};
    @media only screen and (min-width: 640px) {
      font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
      font-size: 16px;
    }
  }
`;
const Strong = styled.strong`
  font-size: inherit;
  font-family: inherit;
  color: inherit;
`;
const Address = styled.address`
  margin-bottom: 5px;
  font-style: italic;
`;
const SUBOL = styled(OL)`
  counter-reset: item;
`;
const GlobalCss = createGlobalStyle`
::marker {
    font-size: 12px;
    color: ${theme.colors.gray_100};
    font-family: 'Raleway',sans-serif;
    @media only screen and (min-width: 640px) {
    font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
    font-size: 16px;
    }
}
  `;
